import React, { useState, useEffect, useContext, useRef, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useHistory, useLocation } from 'react-router-dom'
import { App, Tabs, Card, Empty, Button, Typography, TabsProps, Skeleton } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import isNumber from 'lodash/isNumber'
import sortBy from 'lodash/sortBy'
import uniqBy from 'lodash/uniqBy'
import isNull from 'lodash/isNull'
import pick from 'lodash/pick'
import dayjs from 'dayjs'
import { FetchData, useManualQuery } from 'graphql-hooks'
import * as Sentry from '@sentry/react'
import qs from 'qs'

import Api from '@vacationtracker/shared/services/api'
import { dayjsIterator } from '../../util/dayjs-iterator'
import {
  getLeavesByDateAndStatus,
  getPendingForApproverById,
  getHeatmapData,
  getUserIdsForApprover
} from '../../graphql/custom-queries'
import * as logger from '../../services/logger'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAuthUserSlice, setAuthUser } from '../../store/auth-user-slice'
import { useShouldEnableFeatures } from '../../store/use-should-enable-features'
import { selectLeaveRequestActionEventSlice, setLeaveRequestActionEvent } from '../../store/leave-request-action-event-slice'
import { selectToilRequestActionEventSlice } from '../../store/toil-request-action-event-slice'
import { notificationStore } from '../../context/notificationsContext/store'
import { filterData } from './filter-data'
import { openSupportChat } from '../../util/open-support-chat'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { selectLocaleSlice } from '../../store/locale-slice'

import { getNumberOfDaysInMonth } from '../../components/heatmap-chart/helpers'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { calculatePartDayHours } from '@vacationtracker/shared/functions/get-days-or-hours'
import { apiCache, cacheKey } from '../../util/cache-api-response'
import { showOnboardingV2Modal } from '../../components/onboarding-v2-modal'
import { showApproverOnboardingModal } from '../../components/approver-onboarding-modal'
import { dismissApproverOnboarding } from '../../util/approver-onboarding-storage'

import IntlMessages from '../../util/IntlMessages'
import emptyImage from '../../assets/images/empty.png'
import DenyWithReasonForm from '../../components/deny-with-reason-form'
import HeatmapChart from '../../components/heatmap-chart'
import DashboardSkeleton from '../../components/dashboard-skeleton'
import ZohoPopUpSurvey, { SURVEY_TRIAL_EXTEND, ZohoSurveysEnum } from '../../components/zoho-pop-up-survey'
import { HeatmapEventType, IHeatmapHoliday } from '../../components/heatmap-chart/types'
import FilterAdvanced from '@vacationtracker/shared/components/filter-advanced'
import LeavesBox from '@vacationtracker/shared/components/leaves-box'
import { isToilLeave } from '@vacationtracker/shared/functions/is-toil-leave-request'
import { DEFAULT_WORKING_HOURS_IN_DAY } from '@vacationtracker/shared/data/app-parameters'

import { ILocationShort } from '@vacationtracker/shared/types/location'
import { IFilter } from '@vacationtracker/shared/types/filter'
import { ApproversLevelEnum, ITeamShort } from '@vacationtracker/shared/types/team'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import {
  IGetLeavesByDateAndStatus,
  IGetLeavesByDateAndStatusResponse,
  IGetPendingForApproverByIdData,
  IGetUserIdsForApproverTo,
  IGetUsersForApproverData,
  IHeatmapLeaveRequests
} from '../../types/custom-queries'
import { IHeatMapLeaves, IDashboardLeaves, IApprover, DASHBOARD_SURVEY_STORAGE_KEY } from '../../types/dashboard'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { IDashboardLeaveRequest, IGetLeaveRequestsByDateResponse, LeaveRequestStatus } from '@vacationtracker/shared/types/leave-request'
import { ActiveTabEnum, HeatmapData, HeatmapDataResponse, ILabel } from './types'
import { IToilRequest } from '@vacationtracker/shared/types/toil-request'

const SCHEDULED_LEAVES_LIMIT = 500

const { Paragraph } = Typography

async function loadHeatmapData(getHeatmapDataQuery: FetchData<HeatmapDataResponse, object, object>): Promise<HeatmapData> {
  // Check cache first
  const cached = apiCache.get<HeatmapData>(cacheKey.heatmapData())
  if (cached) {
    return cached
  }

  const abortController = new AbortController()
  const signal = abortController.signal

  try {
    const heatmapData = await getHeatmapDataQuery({
      fetchOptionsOverrides: {
        signal,
      },
    })

    const holidays: IHeatmapHoliday[] = []
    const locations = heatmapData.data?.locations || []

    if (locations.length > 0) {
      locations.forEach((location) => {
        location.holidays.forEach((holidayYear) => {
          holidays.push(
            ...holidayYear.holidays
              .map(holiday => ({
                ...holiday,
                heatmapType: HeatmapEventType.holiday,
                locationName: location.name,
                locationId: location.id,
              })) as IHeatmapHoliday[]
          )
        })
      })
    }

    const result = {
      holidays,
      locations: locations.map(location => ({ id: location.id, name: location.name })),
      departments: heatmapData.data?.departments || [],
      labels: heatmapData.data?.labels || [],
    }

    // Cache for 5 minutes
    apiCache.set(cacheKey.heatmapData(), result, 300)

    return result
  } catch (error) {
    if (error.name === 'AbortError') {
      // console.log('Fetch aborted')
    } else {
      console.error('Error loading heatmap data:', error)
    }
    return {
      holidays: [],
      locations: [],
      departments: [],
      labels: [],
    }
  }
}

async function loadUpcomingLeaves(
  startDate: Date,
  endDate: Date,
  limit = 0,
  nextToken = 'NONE',
  status: LeaveRequestStatus = 'APPROVED',
  getLeavesByDateAndStatusQuery: FetchData<IGetLeavesByDateAndStatus, IGetLeavesByDateAndStatusResponse, object>
): Promise<IGetLeaveRequestsByDateResponse> {
  const response = await getLeavesByDateAndStatusQuery({ variables: {
    dateStart: dayjs(startDate).format('YYYY-MM-DD'),
    dateEnd: dayjs(endDate).format('YYYY-MM-DD'),
    status,
    limit,
    nextToken,
  }})

  // This one is a bit weird, but it happens while dashboard is loading.
  // The error causes the dashboard to stuck in loading state, and show a misleading error message.
  // This is a workaround to prevent the error from being thrown.
  if (
    response.error &&
    response.error.fetchError &&
    response.error.fetchError.toString() === 'Error: fetchData should not be called after hook unmounted'
  ) {
    return {
      leaveRequests: [],
    }
  }

  if (response.error || !response.data || !response.data.getLeaveRequestByDate) {
    throw new Error(JSON.stringify(response))
  }
  return response.data?.getLeaveRequestByDate
}

const DashboardPage: React.FC = () => {
  const { formatMessage } = useIntl()
  const { message, notification, modal } = App.useApp()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { locale } = useAppSelector(selectLocaleSlice)
  const { leaveRequestActionEvent } = useAppSelector(selectLeaveRequestActionEventSlice)
  const { toilRequestActionEvent } = useAppSelector(selectToilRequestActionEventSlice)
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  const dispatch = useAppDispatch()
  const history = useHistory()
  const location = useLocation()
  const isDashboardMounted = useRef(false)
  const onboardingV2Enabled = useFeatureFlagEnabled(FeatureFlagEnum.onboardingV2)
  // Track cancelled leave IDs locally to handle race conditions with eventual consistency
  // This ensures cancelled leaves don't reappear even if backend query returns them as APPROVED
  const cancelledLeaveIdsRef = useRef<Set<string>>(new Set())
  // Track timeout IDs for cleanup on unmount
  const cancelTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map())

  const [openLeavesRequests, setOpenLeavesRequests] = useState<(IDashboardLeaveRequest & IToilRequest)[]>([])
  const [shouldLoadDashboardData, setShouldLoadDashboardData] = useState(true)
  const [upcomingLeaves, setUpcomingLeaves] = useState<IDashboardLeaveRequest[]>([])
  const [heatMapLeaves, setHeatMapLeaves] = useState<IHeatMapLeaves[]>([])
  const [selectedLeaveRequest, setLeaveRequest] = useState<IDashboardLeaves | {}>({})
  const [visibleDenyModal, setVisibleDenyModal] = useState(false)
  const [teams, setTeams] = useState<ITeamShort[]>([])
  const [locations, setLocations] = useState<ILocationShort[]>([])
  const [labels, setLabels] = useState<ILabel[]>([])
  const [heatmapCalendarHolidays, setHeatmapCalendarHolidays] = useState<IHeatmapHoliday[]>([])
  const [heatmapLoading, setHeatmapLoading] = useState(true)
  const [approverToUsers, setApproverToUsers] = useState<IGetUserIdsForApproverTo[]>(authUser.approverTo)
  const [isLoadingDashboardData, setIsLoadingDashboardData] = useState(false)
  const [filterDataLoading, setFilterDataLoading] = useState(true)
  const [openLeavesLoading, setOpenLeavesLoading] = useState(true)
  const [upcomingLeavesLoading, setUpcomingLeavesLoading] = useState(true)
  const [filter, setFilter] = useState<IFilter>({
    locationIds: [],
    teamIds: [],
    labelIds: [],
  })
  const [seenApproverOnboarding, setSeenApproverOnboarding] = useState(authUser.hasSeenApproverOnboarding)

  const today = new Date()
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth())
  const [selectedYear, setSelectedYear] = useState(today.getFullYear())
  const [leaveRequestsNextPaginationToken, setLeaveRequestsNextPaginationToken] = useState<string | undefined>('')
  const [showZohoSurvey, setShowZohoSurvey] = useState(false)
  const [heatmapLeaveRequests, setHeatmapLeaveRequests] = useState<IHeatmapLeaveRequests[] | null>(null)
  const [countryHolidays, setCountryHolidays] = useState<IHeatmapHoliday[]>([])
  const isHeatmapFreshLoadRef = useRef(true)
  const shouldEnableFeatures = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.labels)
  const queryParams = React.useMemo(() => qs.parse(location.search, { ignoreQueryPrefix: true }), [location.search])
  // Removed showOnboardingModal state - using imperative modal instead

  const [ getHeatmapDataQuery ] = useManualQuery<HeatmapDataResponse>(getHeatmapData)
  const [ getLeavesByDateAndStatusQuery ] = useManualQuery<IGetLeavesByDateAndStatus, IGetLeavesByDateAndStatusResponse>(getLeavesByDateAndStatus)
  const [ getPendingForApproverByIdQuery ] = useManualQuery<IGetPendingForApproverByIdData>(getPendingForApproverById)
  const [ getUserIdsForApproverQuery ] = useManualQuery<IGetUsersForApproverData>(getUserIdsForApprover)

  // Memoize filtered results to prevent unnecessary recalculations
  const filteredOpenLeavesRequests = React.useMemo(() => filterData(filter, openLeavesRequests),
    [filter, openLeavesRequests]
  )

  const filteredUpcomingLeaves = React.useMemo(() =>
    filterData(filter, upcomingLeaves).sort((a, b) => a.startDate < b.startDate ? -1 : 1), [filter, upcomingLeaves]
  )

  const onHeatmapPreviousMonth = useCallback(() => {
    if (selectedMonth === 0) {
      setSelectedMonth(11)
      setSelectedYear(selectedYear - 1)
    } else {
      setSelectedMonth(selectedMonth - 1)
    }
    setHeatmapLoading(true)
  }, [selectedMonth, selectedYear])

  const onHeatmapNextMonth = useCallback(() => {
    if (selectedMonth === 11) {
      setSelectedMonth(0)
      setSelectedYear(selectedYear + 1)
    } else {
      setSelectedMonth(selectedMonth + 1)
    }
    setHeatmapLoading(true)
  }, [selectedMonth, selectedYear])

  const handleFilter = useCallback((filterArg: IFilter) => {
    if(!isEqual(filterArg, filter)) {
      setFilter(filterArg)
      // Trigger loading state to ensure proper re-render cycle for filter changes
      setHeatmapLoading(true)
    }

    // Filtered values are now memoized, filter state will trigger recalculation
  }, [filter])

  useEffect(() => {
    // This is (probably stupid) workaround to prevent the "Warning: Can't perform a React state update on an unmounted component."
    isDashboardMounted.current = true

    // Fetch fresh approver data to ensure permissions are up-to-date
    const fetchApproverData = async () => {
      try {
        const response = await getUserIdsForApproverQuery({ variables: { id: authUser.id } })
        if (response.data?.getUser?.approverTo && isDashboardMounted.current) {
          setApproverToUsers(response.data.getUser.approverTo)
        }
      } catch (err) {
        logger.error('Error fetching approver data:', err)
      }
    }

    fetchApproverData()

    return () => {
      isDashboardMounted.current = false
      // Clean up all pending cancel timeouts to prevent memory leaks
      cancelTimeoutsRef.current.forEach((timeoutId) => {
        clearTimeout(timeoutId)
      })
      cancelTimeoutsRef.current.clear()
    }
  }, [authUser.id])

  // Effect for filter changes
  useEffect(() => {
    handleFilter(filter)
  }, [filter, handleFilter])

  // Effect for initial data loading
  useEffect(() => {
    if (shouldLoadDashboardData) {
      loadDashboardData(selectedYear, selectedMonth)
      // Load open leaves in parallel if user has approver permissions
      if (authUser.role !== 'User' || authUser.approverTo.length > 0) {
        getOpenLeaves(getPendingForApproverByIdQuery)
      }
    }
  }, [shouldLoadDashboardData])

  useEffect(() => {
    if (queryParams.showSurvey === 'true' && !authCompany?.billing?.couponCodes?.includes(SURVEY_TRIAL_EXTEND)) {
      setShowZohoSurvey(true)
    }
  }, [queryParams, authCompany])

  // Remove showSurvey query param when survey is closed
  useEffect(() => {
    if (!showZohoSurvey && queryParams.showSurvey === 'true') {
      const currentParams = qs.parse(location.search, { ignoreQueryPrefix: true })
      delete currentParams.showSurvey
      const newSearch = qs.stringify(currentParams)
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : '',
      })
    }
  }, [showZohoSurvey, queryParams.showSurvey, location.pathname, location.search, history])

  // Handle onboarding v2 modal
  const modalInstanceRef = useRef<any>(null)
  const isShowingModalRef = useRef(false)

  // Memoize the onClose callback to prevent recreation
  const handleOnboardingModalClose = useCallback(() => {
    // Remove the onboarding query param after closing
    const currentParams = qs.parse(location.search, { ignoreQueryPrefix: true })
    delete currentParams.onboardingV2
    const newSearch = qs.stringify(currentParams)
    history.push({
      pathname: location.pathname,
      search: newSearch ? `?${newSearch}` : '',
    })

    // Destroy modal reference
    if (modalInstanceRef.current) {
      modalInstanceRef.current = null
    }
  }, [history, location.pathname, location.search])

  const handleApproverOnboardingModalClose = useCallback(() => {
    setSeenApproverOnboarding(true)
    dispatch(setAuthUser({ ...authUser, hasSeenApproverOnboarding: true }))
    // Destroy modal reference
    if (modalInstanceRef.current) {
      modalInstanceRef.current = null
    }
  }, [])

  // Separate effect just for onboarding modals - only runs when these specific values change
  useEffect(() => {
    // Early exit if modal already exists or conditions not met
    if (modalInstanceRef.current || isShowingModalRef.current) return
    if (filterDataLoading || heatmapLoading) return
    if (authCompany?.integrationOnboardings?.some(onboarding => onboarding.name === 'ADP')) return

    // Check for approver onboarding first
    if ((authUser.approverToTeams?.length || 0) > 0 && !isNull(authUser.hasSeenApproverOnboarding) && !authUser.hasSeenApproverOnboarding) {
      // Don't show approver onboarding for Freemium users
      if (authCompany?.plan === 'Freemium') {
        // Mark as seen to prevent showing it again
        setSeenApproverOnboarding(true)

        // Also mark localStorage progress as complete (similar to admin onboarding)
        if (authUser?.id && authCompany?.id) {
          dismissApproverOnboarding(authUser.id, authCompany.id)
        }

        const isAdmin = authUser.role === 'Admin'
        const userUpdated = {
          ...pick(authUser, 'name', 'locale', 'hourFormat'),
          imageUrl: authUser.imageUrl || undefined,
          hasSeenApproverOnboarding: true,
          ...(isAdmin && {
            isNameLocked: authUser.isNameLocked || false,
            email: authUser.email,
            startDate: authUser.startDate,
            isAdmin: isAdmin,
          }),
        }
        Api.post('/core/event', {
          eventType: 'USER_UPDATED',
          eventGroup: 'USER',
          userId: authUser.id,
          ...userUpdated,
        })
        return
      }

      setSeenApproverOnboarding(true)
      // Show approver onboarding
      isShowingModalRef.current = true

      // Get all department names where user is an approver
      const departmentNames = authUser.approverToTeams
        ?.map(team => team.name)
        .filter((name): name is string => Boolean(name && typeof name === 'string')) || []
      const isAdmin = authUser.role === 'Admin'
      // Update user to mark approver onboarding as seen immediately
      const userUpdated = {
        ...pick(authUser, 'name', 'locale', 'hourFormat'),
        imageUrl: authUser.imageUrl || undefined,
        hasSeenApproverOnboarding: true,
        ...(isAdmin && {
          isNameLocked: authUser.isNameLocked || false,
          email: authUser.email,
          startDate: authUser.startDate,
          isAdmin: isAdmin,
        }),
      }
      Api.post('/core/event', {
        eventType: 'USER_UPDATED',
        eventGroup: 'USER',
        userId: authUser.id,
        ...userUpdated,
      })

      modalInstanceRef.current = showApproverOnboardingModal(modal, authUser, locale, departmentNames, {
        onClose: () => {
          handleApproverOnboardingModalClose()
          isShowingModalRef.current = false
        },
        onExploreFirst: () => {
          handleApproverOnboardingModalClose()
          isShowingModalRef.current = false
        },
        onMinimize: () => {
          handleApproverOnboardingModalClose()
          isShowingModalRef.current = false
        },
        companyId: authCompany?.id || '',
      })
      return
    }

    // Regular onboarding v2 flow
    if (queryParams?.onboardingV2 === 'true' && onboardingV2Enabled) {
      // Prevent concurrent modal creation
      isShowingModalRef.current = true

      // Create heatmapData object from existing state
      const heatmapData = {
        locations: locations,
        departments: teams,
        labels: labels,
        holidays: countryHolidays,
      }

      // Show the modal
      modalInstanceRef.current = showOnboardingV2Modal(modal, authUser, locale, heatmapData, {
        onClose: () => {
          handleOnboardingModalClose()
          isShowingModalRef.current = false
        },
        onExploreFirst: () => {
          handleOnboardingModalClose()
          isShowingModalRef.current = false
        },
        onMinimize: () => {
          handleOnboardingModalClose()
          isShowingModalRef.current = false
        },
      })
    }

    // Cleanup function
    return () => {
      if (modalInstanceRef.current) {
        modalInstanceRef.current.destroy()
        modalInstanceRef.current = null
      }
    }
  }, [
    queryParams?.onboardingV2,
    onboardingV2Enabled,
    seenApproverOnboarding,
    filterDataLoading,
    heatmapLoading,
    locations,
    teams,
    labels,
    heatmapCalendarHolidays,
    authUser,
    authCompany?.id,
    authCompany?.plan, // Add plan to dependencies
  ])

  useEffect(() => {
    if (isLoadingDashboardData) {
      loadDashboardData(selectedYear, selectedMonth)
    }
  }, [selectedYear, selectedMonth])

  useEffect(() => {
    if (leaveRequestActionEvent || toilRequestActionEvent) {
      // Use targeted cache invalidation instead of clearing everything
      // Only invalidate leave-related caches, keep heatmap data (locations/teams/labels)
      apiCache.invalidatePattern('leaves-')
      apiCache.invalidate(cacheKey.pendingForApprover(authUser.id))

      // Set ref to indicate this is a fresh load that should bypass cache
      isHeatmapFreshLoadRef.current = true
      getOpenLeaves(getPendingForApproverByIdQuery)
      // Always reload dashboard data to refresh heatmap when leaves are approved/denied
      loadDashboardData(selectedYear, selectedMonth)
      dispatch(setLeaveRequestActionEvent(null))
    }
  }, [leaveRequestActionEvent, toilRequestActionEvent])

  useEffect(() => {
    if (heatmapLeaveRequests && heatmapLeaveRequests.length > 0) {
      prepareLeavesForHeatMap(selectedYear, selectedMonth)
    }

    // Set loading to false after preparing leaves for heatmap (or when there are no leaves)
    if (heatmapLeaveRequests !== null) {
      setHeatmapLoading(false)
    }
  }, [heatmapLeaveRequests, selectedYear, selectedMonth, filter])


  const getOpenLeaves = async (getPendingForApproverByIdQuery: FetchData<IGetPendingForApproverByIdData, {
    approverId: string
  }, object>) => {
    try {
      // Check cache first for pending requests
      const cacheKeyValue = cacheKey.pendingForApprover(authUser.id)
      const cached = apiCache.get<any>(cacheKeyValue)
      if (cached) {
        const toilRequest: IToilRequest[] = cached.getPendingToilForApprover.map(toil => {
          let workingDays = dayjs(toil.endDate).diff(dayjs(toil.startDate), 'day') || 1
          if (toil.partDay) {
            const toilHours = calculatePartDayHours(toil.partDay)
            if (toilHours > 0) {
              workingDays = toilHours / DEFAULT_WORKING_HOURS_IN_DAY
            }
          }
          if (isNumber(toil.partDayEndHour) && isNumber(toil.partDayStartHour)) {
            const workingHours = toil.partDayEndHour - toil.partDayStartHour
            if (workingHours > 0) {
              workingDays = workingHours / DEFAULT_WORKING_HOURS_IN_DAY
            }
          }
          return {
            ...toil,
            workingDays,
          }
        })
        const requests = sortBy([
          ...toilRequest,
          ...cached.getPendingForApprover,
        ], ['startDate'      ])
        setOpenLeavesRequests(requests as (IDashboardLeaveRequest & IToilRequest)[])
        setOpenLeavesLoading(false)
        return
      }

      const response = await getPendingForApproverByIdQuery()
      if (!isDashboardMounted.current) {
        return
      }
      if (!response.data || response.error) throw response.error
      const toilRequest: IToilRequest[] = response.data.getPendingToilForApprover.map(toil => {
        let workingDays = dayjs(toil.endDate).diff(dayjs(toil.startDate), 'day') || 1
        if (toil.partDay) {
          const toilHours = calculatePartDayHours(toil.partDay)
          if (toilHours > 0) {
            workingDays = toilHours / DEFAULT_WORKING_HOURS_IN_DAY
          }
        }
        if (isNumber(toil.partDayEndHour) && isNumber(toil.partDayStartHour)) {
          const workingHours = toil.partDayEndHour - toil.partDayStartHour
          if (workingHours > 0) {
            workingDays = workingHours / DEFAULT_WORKING_HOURS_IN_DAY
          }
        }
        return {
          ...toil,
          workingDays,
        }
      })
      const requests = sortBy([
        ...toilRequest,
        ...response.data.getPendingForApprover,
      ], ['startDate'      ])
      setOpenLeavesRequests(requests as (IDashboardLeaveRequest & IToilRequest)[])

      setOpenLeavesLoading(false)

      // Cache the response for 2 minutes
      apiCache.set(cacheKeyValue, response.data, 120)
    } catch (err) {
      logger.error('error fetching open leaves', err)
      setOpenLeavesLoading(false)
    }
  }

  const loadMoreUpcomingLeaves = async (
    isInitial = false,
    now = today,
    year = today.getFullYear(),
    month = today.getMonth(),
    nextToken?: string
  ) => {
    const todaysDate = now
    const endOf6stMonth = dayjs(`${year}-${month}-15`).add(24, 'month').endOf('month').toDate()
    try {
      const response = await loadUpcomingLeaves(todaysDate, endOf6stMonth, SCHEDULED_LEAVES_LIMIT, nextToken, 'APPROVED', getLeavesByDateAndStatusQuery)
      if (!isDashboardMounted.current) {
        return
      }
      setLeaveRequestsNextPaginationToken(response.nextToken)

      const newUpcomingLeaves = response.leaveRequests.filter(leave => dayjs().isBefore(dayjs(leave.startDate)))

      if (isInitial) {
        setUpcomingLeaves(newUpcomingLeaves)
        setUpcomingLeavesLoading(false)
      } else {
        const allUpcomingLeaves = uniqBy([ ...upcomingLeaves, ...newUpcomingLeaves ], 'id')
        setUpcomingLeaves(allUpcomingLeaves)
      }
    } catch (err) {
      Sentry.captureException(err?.error || err)

      notification.error({
        key: 'calendar-loading-error',
        message: formatMessage({ id: 'error.leaveRequestsLoadingError.title' }),
        description: (<>
          <Paragraph>
            {
              formatMessage({id: 'error.leaveRequestsLoadingError.description'}, {
                link: (chunks: React.ReactNode) => <a onClick={() => openSupportChat()}>{chunks}</a>,
              })
            }
          </Paragraph>
        </>),
        btn: (<Button onClick={() => {
          window.location.reload()
        }}>
          { formatMessage({ id: 'app.reload'} )}
        </Button>),
        duration: 0,
      })
    }
  }

  async function loadHeatMapLeaves (
    dateStart: string,
    dateEnd: string,
    status: LeaveRequestStatus,
    limit = 0,
    nextToken = 'NONE'
  ): Promise<string | undefined> {
    const response = await loadUpcomingLeaves(dayjs(dateStart).toDate(), dayjs(dateEnd).toDate(), limit, nextToken, status, getLeavesByDateAndStatusQuery)
    if (!isDashboardMounted.current) {
      return
    }
    setHeatmapLeaveRequests(leavesOrNull => {
      const leaves = leavesOrNull || []
      return uniqBy([...response.leaveRequests as [], ...leaves], 'id')
    })
    if (response.nextToken) {
      return await loadHeatMapLeaves(dateStart, dateEnd, status, limit, response.nextToken)
    }
  }

  const prepareLeavesForHeatMap = (year = today.getFullYear(), month = today.getMonth()) => {
    const monthStart = dayjs().year(year).month(month).startOf('month').format('YYYY-MM-DD')
    const monthEnd = dayjs().year(year).month(month).endOf('month').format('YYYY-MM-DD')
    const numberOfDays = getNumberOfDaysInMonth(year, month)
    let leaves = Array.from({ length: numberOfDays }, () => ({
      value: 0,
      leaves: [],
    })) as unknown as IHeatMapLeaves[]
    if (heatmapLeaveRequests && heatmapLeaveRequests.length > 0) {
      heatmapLeaveRequests.forEach((leave) => {
        // Skip cancelled, denied, and deleted leaves - they shouldn't appear in heatmap
        // Also skip leaves that we've locally marked as cancelled (handles race conditions with eventual consistency)
        if (leave.status === 'CANCELLED' || leave.status === 'DENIED' || leave.status === 'DELETED' || cancelledLeaveIdsRef.current.has(leave.id)) {
          return
        }
        for (const d of dayjsIterator(leave.startDate, leave.endDate)) {
          const dayOfTheMonth = dayjs(d).get('date')
          const match = leaves.filter(item => item.formatDate === dayjs(d).format('YYYY-MM-DD'))
          const parsedDate = dayjs(d).format('YYYY-MM-DD')

          const filtered = filterData(filter, [leave])

          if (!filtered.length) {
            return
          }

          if (match.length > 0) {
            leaves = leaves.map((item: IHeatMapLeaves) => {
              return {
                ...item,
                value: item.formatDate === parsedDate ? item.value + 1 : item.value,
                leaves: item.formatDate === parsedDate ? [...item.leaves, leave] : item.leaves,
              }
            })
          } else if (dayjs(d).isBetween(monthStart, monthEnd, 'day', '[]')) {
            leaves[dayOfTheMonth - 1] = {
              formatDate: dayjs(d).format('YYYY-MM-DD'),
              date: dayjs(d).unix(),
              value: 1,
              leaves: [leave],
            }
          }
        }
      })
    }
    setHeatMapLeaves(leaves)
  }

  const loadDashboardData = async (year = today.getFullYear(), month = today.getMonth()) => {
    const monthStart = dayjs().year(year).month(month).startOf('month').format('YYYY-MM-DD')
    const monthEnd = dayjs().year(year).month(month).endOf('month').format('YYYY-MM-DD')

    // Reset heatmap leave requests to ensure fresh data load (prevents stale cancelled leaves from persisting)
    setHeatmapLeaveRequests(null)

    // Start all data fetching in parallel
    const [heatmapResult, heatmapLeavesResult, upcomingLeavesResult] = await Promise.allSettled([
      // Fetch heatmap data
      loadHeatmapData(getHeatmapDataQuery),

      // Fetch heatmap leaves
      loadHeatMapLeaves(monthStart, monthEnd, 'APPROVED', SCHEDULED_LEAVES_LIMIT, 'NONE'),

      // Fetch upcoming leaves
      loadMoreUpcomingLeaves(true),
    ])

    if (!isDashboardMounted.current) {
      return
    }

    // Process heatmap data if successful
    if (heatmapResult.status === 'fulfilled') {
      const heatmapData = heatmapResult.value
      setLocations(heatmapData.locations.map(l => ({name: l.name, id: l.id})))
      setTeams(heatmapData.departments)
      setLabels(heatmapData.labels)

      const heatmapHolidays = heatmapData.holidays
      // We only use this for the onboarding modal
      setCountryHolidays(heatmapHolidays)

      const holidays: IHeatmapHoliday[] = heatmapHolidays.filter(holiday => {
        const holidayYear = holiday.date.slice(0, 4)
        const holidayMonth = holiday.date.slice(5, 7)
        return (
          Number(holidayYear) === year
          && Number(holidayMonth) === month + 1
          && (
            filter.locationIds.length === 0
            || filter.locationIds.length > 0 && filter.locationIds.includes(holiday.locationId)
          )
        )
      })

      setHeatmapCalendarHolidays(holidays)
      setFilterDataLoading(false)
    } else {
      logger.error('Failed to load heatmap data:', heatmapResult.reason)
      setFilterDataLoading(false)
    }

    // Mark data loading flags - heatmap loading will be set false by useEffect after data is processed
    setShouldLoadDashboardData(false)
    setIsLoadingDashboardData(true)

    // Handle any errors
    const errors = [heatmapResult, heatmapLeavesResult, upcomingLeavesResult]
      .filter(result => result.status === 'rejected')
      .map(result => (result as PromiseRejectedResult).reason)

    if (errors.length > 0) {
      logger.error('Dashboard loading errors:', errors)
      // Only show error notification if all requests failed
      if (errors.length === 3) {
        notification.error({
          key: 'calendar-loading-error',
          message: formatMessage({ id: 'error.leaveRequestsLoadingError.title' }),
          description: (<>
            <Paragraph>
              {
                formatMessage({id: 'error.leaveRequestsLoadingError.description'}, {
                  link: (chunks: React.ReactNode) => <a onClick={() => openSupportChat()}>{chunks}</a>,
                })
              }
            </Paragraph>
          </>),
          btn: (<Button onClick={() => {
            window.location.reload()
          }}>
            { formatMessage({ id: 'app.reload'} )}
          </Button>),
          duration: 0,
        })
      }
    }
  }

  const onUpdateLeaveRequest = async (leave, leaveStatus, statusReason?: string) => {
    const upcomingLeaves: IDashboardLeaveRequest[] = []
    try {
      let response
      if (isToilLeave(leave.id)) {
        response = await Api.post('/core/event', {
          eventType: leaveStatus ? 'TOIL_REQUEST_APPROVED' : 'TOIL_REQUEST_DENIED',
          eventGroup: 'USER_TOIL_REQUEST',
          userId: leave.user.id,
          toilRequestId: leave.id,
          statusReason,
        })
      } else {
        response = await Api.post('/core/event', {
          eventType: 'LEAVE_REQUEST_STATUS_CHANGED',
          eventGroup: 'USER_LEAVE_REQUEST',
          userId: leave.user.id,
          leaveRequestId: leave.id,
          statusReason,
          leaveStatus: leaveStatus ? 'APPROVED' : 'DENIED',
        })
      }

      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'app.updatedInProgress' }),
        icon: (<LoadingOutlined />),
        duration: 0,
      })

      setActionNotifications([
        ...actionNotifications,
        response.correlationId,
      ])

      setVisibleDenyModal(false)
      setOpenLeavesRequests(openLeavesRequests.filter((item) => item.id !== leave.id))

      // Invalidate cache immediately (defensive: ensures fresh data available)
      apiCache.invalidatePattern('leaves-')
      apiCache.invalidate(cacheKey.pendingForApprover(authUser.id))
      // Data will be reloaded when action notification arrives (see leaveRequestActionEvent useEffect)
    } catch (error) {
      message.error(error)
    }
  }

  const onCancelLeave = async (leave) => {
    try {
      // Track this leave as cancelled locally to handle race conditions
      // This ensures it won't reappear even if backend query returns it as APPROVED
      cancelledLeaveIdsRef.current.add(leave.id)

      const response = await Api.post('/core/event', {
        eventType: 'LEAVE_REQUEST_CANCELLED',
        eventGroup: 'USER_LEAVE_REQUEST',
        leaveRequestId: leave.id,
        userId: leave.user.id,
      })

      const stateData = upcomingLeaves.filter(upcomingLeave => upcomingLeave.id !== leave.id)
      setUpcomingLeaves(stateData)
      setHeatmapLeaveRequests(leavesOrNull => {
        const leaves = leavesOrNull || []
        return uniqBy(leaves.filter((item) => item.id !== leave.id), 'id')
      })

      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'user.cancelLeaveRequestInProgress' }),
        icon: (<LoadingOutlined />),
        duration: 0,
      })

      setActionNotifications([ ...actionNotifications, response.correlationId ])

      // Invalidate cache when leave is cancelled
      apiCache.invalidatePattern('leaves-') // Clear all leave-related caches
      apiCache.invalidate(cacheKey.pendingForApprover(authUser.id))

      // Safety timeout: if Lambda fails, the error goes to Notifications component (different subscription)
      // and DashboardPage never knows about it. After 30 seconds, remove the ID from our local tracking
      // so the next data refresh shows the true state from the database.
      const timeoutId = setTimeout(() => {
        cancelledLeaveIdsRef.current.delete(leave.id)
        cancelTimeoutsRef.current.delete(leave.id)
      }, 30000)
      cancelTimeoutsRef.current.set(leave.id, timeoutId)
    } catch (error) {
      // Cancel failed - remove the ID from our local tracking so the leave reappears
      cancelledLeaveIdsRef.current.delete(leave.id)
      // Clear any existing timeout for this leave
      const existingTimeout = cancelTimeoutsRef.current.get(leave.id)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
        cancelTimeoutsRef.current.delete(leave.id)
      }
      // Refresh data to restore the leave in UI
      loadDashboardData(selectedYear, selectedMonth)
      logger.warning('cancel leave error', error)
      message.error(error)
    }
  }

  const [activeTab] = useState(() => {
    if (authUser.role === 'User') {
      return ActiveTabEnum.peopleOffToday
    }
    return ActiveTabEnum.openLeavesRequests
  })

  const dashboardTabItems: TabsProps['items'] = [
    // UPCOMING DAYS OFF TAB
    {
      key: ActiveTabEnum.upcomingDaysOff,
      label: <>
        <IntlMessages id="app.scheduledLeaves" />
        {isLoadingDashboardData && ` (${filteredUpcomingLeaves.length}${leaveRequestsNextPaginationToken && filteredUpcomingLeaves.length >= 2 ? '+' : ''})`}
      </>,
      children: <>
        {filteredUpcomingLeaves.length > 0 ?
          filteredUpcomingLeaves.map(leave => {
            return (<LeavesBox
              leaveRequestId={leave.id}
              userId={leave.user.id}
              userImage={leave.user.imageUrl}
              userName={leave.user.name}
              daysList={leave.daysList || []}
              leaveTypeName={leave.leavePolicy.leaveType.name}
              startDate={leave.startDate}
              endDate={leave.endDate}
              leaveTypeColor={leave.leavePolicy.leaveType.color}
              leaveStatus="APPROVED"
              type="UPCOMING"
              role={authUser.role}
              reason={leave.reason}
              partDayStartHour={leave.partDayStartHour}
              partDayEndHour={leave.partDayEndHour}
              partDay={leave.partDay}
              approversName={leave.approverList ? leave.approverList.map(approver => approver.name) : undefined}
              autoApproved={leave.autoApproved}
              workingDays={leave.workingDays}
              hasWorkingHoursSchedule={leave.hasWorkingHoursSchedule}
              isPartDay={leave.isPartDay}
              key={leave.id}
              isHiddenLeaveType={leave.leavePolicy.hideLeaveType}
              handleDenyWithReason={() => { setVisibleDenyModal(!visibleDenyModal); setLeaveRequest(leave) }}
              onUpdateLeaveRequest={() => { onUpdateLeaveRequest(leave, false) }}
              onCancelLeave={() => { onCancelLeave(leave) }}
              approverToUsers={approverToUsers}
              hourlyLeaveAccounting={Boolean(authCompany?.hourlyLeaveAccounting)}
              locale={locale.locale}
              hourFormat={authUser.hourFormat}
              isToil={isToilLeave(leave.id)}
              approvedBySubstituteApprover={leave.approvedBySubstituteApprover}
              editLeaves={true}
            />)
          }) :
          <Empty
            description={formatMessage({ id: 'dashboard.noOneIsTackingLeaves' })}
            image={<img src={emptyImage} />}
            styles={{ image: { width: '180px', height: '180px', margin: '0 auto' } }}
          />
        }
        {leaveRequestsNextPaginationToken &&
          <Button onClick={() => {loadMoreUpcomingLeaves(false, new Date(), new Date().getFullYear(), new Date().getMonth(), leaveRequestsNextPaginationToken)}}>
            <IntlMessages id="app.showMore" />
          </Button>
        }
      </>,
    },
  ]

  // PREPEND THE OPEN LEAVE REQUESTS TAB FOR ADMINS AND APPROVERS
  if (authUser.role !== 'User' || (authUser.role === 'User' && openLeavesRequests.length > 0)) {
    dashboardTabItems.unshift({
      key: ActiveTabEnum.openLeavesRequests,
      label: <><IntlMessages id="dashboard.openLeavesRequests" /> {`(${filteredOpenLeavesRequests.length})`}</>,
      children: filteredOpenLeavesRequests.length > 0 ?
        filteredOpenLeavesRequests.map(leave => {
          return (<LeavesBox
            leaveRequestId={leave.id}
            userId={leave.user.id}
            userImage={leave.user.imageUrl}
            userName={leave.user.name}
            daysList={leave.daysList || []}
            leaveTypeName={leave.leavePolicy.leaveType.name}
            startDate={leave.startDate}
            endDate={leave.endDate}
            leaveTypeColor={leave.leavePolicy.leaveType.color}
            leaveStatus="OPEN"
            type="REQUEST"
            workingDays={leave.workingDays}
            hasWorkingHoursSchedule={leave.hasWorkingHoursSchedule}
            isPartDay={leave.isPartDay}
            role={authUser.role}
            partDayStartHour={leave.partDayStartHour}
            partDayEndHour={leave.partDayEndHour}
            partDay={leave.partDay}
            approvedLevel={leave.approvedLevel}
            approver={(Array.isArray(leave.approver) ? leave.approver[0] : leave.approver) as IApprover | undefined}
            approversName={leave.approverList ? leave.approverList.map(approver => approver.name) : undefined}
            isMultilevelApproval={Boolean(leave.user.team?.approvers.find(approver =>
              approver?.approverLevels?.includes(ApproversLevelEnum.SECOND)
            ))}
            // We need to append the approved level to the key to force re-render of the component when the first level approves the request
            key={`${leave.id}-${leave.approvedLevel}`}
            isHiddenLeaveType={leave.leavePolicy.hideLeaveType}
            reason={leave.reason}
            handleDenyWithReason={() => { setVisibleDenyModal(!visibleDenyModal); setLeaveRequest(leave) }}
            onUpdateLeaveRequest={(type) => { onUpdateLeaveRequest(leave, type) }}
            editLeaves={true}
            approverToUsers={approverToUsers}
            isEdited={leave.isEdited}
            hourlyLeaveAccounting={Boolean(authCompany?.hourlyLeaveAccounting)}
            locale={locale.locale}
            hourFormat={authUser.hourFormat}
            isToil={isToilLeave(leave.id)}
          />)
        }) :
        <Empty
          description={formatMessage({ id: 'dashboard.noOpenLeavesRequests' })}
          image={<img src={emptyImage} />}
          styles={{ image: { width: '200px', height: '200px', margin: '0 auto' } }}
        />,
    })
  }

  // Show skeleton loader on initial load
  if (shouldLoadDashboardData) {
    return <DashboardSkeleton />
  }

  return (
    <>
      {/* Dashboard content loads progressively as data arrives */}
      <div className="dashboard-filters dashboard-width">
        <FilterAdvanced
          page='dashboard'
          data={{
            Locations: locations,
            Departments: teams,
            Labels: labels,
          }}
          onChangeFilter={handleFilter}
          showLabels={shouldEnableFeatures}
          currentUserId={authUser.id}
        />
      </div>

      <Card className="heatmap dashboard-width">
        {heatmapLoading ?
          <div style={{ padding: 20 }}>
            <Skeleton.Input style={{ width: 200, marginBottom: 16 }} active />
            <Skeleton active paragraph={{ rows: 5 }} />
          </div> :
          <HeatmapChart
            leaves={heatMapLeaves}
            onPreviousMonth={onHeatmapPreviousMonth}
            onNextMonth={onHeatmapNextMonth}
            month={selectedMonth}
            year={selectedYear}
            workingDays={authUser.workWeekType === 'LOCATION' ? authUser.location.workWeek : authUser.workWeek}
            holidays={heatmapCalendarHolidays}
            isHeatmapLoading={false}
            role={authUser.role}
            onCancelLeave={onCancelLeave}
            approverToUsers={approverToUsers}
            hourlyLeaveAccounting={Boolean(authCompany?.hourlyLeaveAccounting)}
            locale={locale.locale}
            cancelledLeaveIds={cancelledLeaveIdsRef.current}
          />
        }
      </Card>

      <Card className="dashboard-leaves-info dashboard-width">
        <Tabs defaultActiveKey={activeTab} items={dashboardTabItems} />
      </Card>
      {visibleDenyModal && !isEmpty(selectedLeaveRequest) &&
        <DenyWithReasonForm
          visibleModal={visibleDenyModal}
          leaveRequest={selectedLeaveRequest as IDashboardLeaves}
          handleCancel={() => (setVisibleDenyModal(false))}
          onSave={(data) => (onUpdateLeaveRequest(data.leaveRequest, false, data.statusReason))}
        />
      }
      {authUser.role === 'Admin' && authCompany?.trialPeriod === 'in_trial' && authCompany.billing?.paymentProcessor === 'microsoft-billing'
        ? <ZohoPopUpSurvey
          surveyId={ZohoSurveysEnum.trialSurvey}
          timeOffsetMinutes={720} // 12h
          remindMeLaterTimeOffset={2880} //48h
          surveyLocalStorageKey={DASHBOARD_SURVEY_STORAGE_KEY}
        />
        : <ZohoPopUpSurvey
          surveyId={ZohoSurveysEnum.trialExtendSurvey}
          timeOffsetMinutes={720}
          remindMeLaterTimeOffset={2880}
          surveyLocalStorageKey={DASHBOARD_SURVEY_STORAGE_KEY}
          showZohoSurvey={showZohoSurvey}
          setShowZohoSurvey={setShowZohoSurvey}
        />
      }

    </>
  )
}

export default DashboardPage
